/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { SidebarContext } from '../../../../context/SidebarContext';
import { getLocalStorageItem } from '../../../../utils/helper';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  EntityHeader,
  BackLink,
  Chip,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import { challengePhaseChipClass } from './challengesTheme';
import {
  fetchCompanyChallengeUserDetail,
  formatBigNumber,
  lastNDaysLabels
} from './companyChallengesApi';

const dateLabel = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '-';

/**
 * Company Challenges → one employee's/member's challenge profile.
 *
 * Three bands: their headline numbers, their tracked daily series, and the
 * challenges themselves — one row each with how far they got, what they
 * tracked inside it, and anything they won.
 *
 * The backend 404s any user who doesn't belong to this company, so there is no
 * ownership check to repeat here.
 */
const CChallengesUserDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isB2BFansMode } = useContext(SidebarContext);
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      setLoader(false);
      return undefined;
    }
    let mounted = true;
    setLoader(true);
    fetchCompanyChallengeUserDetail(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [userId, isB2BFansMode]);

  const volumeLabel = detail?.primaryDataTypeLabel || 'Steps';
  const volumeUnit = volumeLabel.toLowerCase();
  const dailySeries = detail?.dailySeries || [];
  const hasSeries = dailySeries.some((value) => value > 0);

  const seriesOptions = {
    chart: { ...CHART_BASE, type: 'area' },
    colors: [CHART_COLORS.brand],
    stroke: { curve: 'smooth', width: 2 },
    fill: AREA_FILL,
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: lastNDaysLabels(dailySeries.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} ${volumeUnit}` } }
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Challenge Profile | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      <BackLink
        label={isB2BFansMode ? 'Back to Members' : 'Back to Employees'}
        onClick={() => navigate(`/${slug}/challenges/insights/users`)}
      />

      {!loader && !detail ? (
        <Panel className="mt-4">
          <PanelEmpty>This user&apos;s challenge profile could not be loaded.</PanelEmpty>
        </Panel>
      ) : null}

      {detail ? (
        <>
          <div className="mt-4">
            <EntityHeader
              image={detail.user?.profileUrl}
              name={detail.user?.name}
              subtitle={detail.user?.email}
              chips={
                detail.rank ? (
                  <Chip className="bg-shoorah-lightIndigo text-shoorah-primary">
                    Rank #{detail.rank} of {detail.totalParticipants}
                  </Chip>
                ) : null
              }
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
            <StatCard
              icon={STAT_ICONS.catalog}
              title="Challenges Joined"
              value={formatBigNumber(detail.challengesJoined)}
            />
            <StatCard
              icon={STAT_ICONS.completions}
              title="Completed"
              value={formatBigNumber(detail.challengesCompleted)}
            />
            <StatCard
              icon={STAT_ICONS.sessions}
              title={`Total ${volumeLabel}`}
              value={formatBigNumber(detail.totalVolume)}
            />
            <StatCard
              icon={STAT_ICONS.streak}
              title="Active Days"
              value={formatBigNumber(detail.activeDays)}
            />
            <StatCard
              icon={STAT_ICONS.score}
              title="Daily Average"
              value={formatBigNumber(detail.avgDailyVolume)}
            />
            <StatCard
              icon={STAT_ICONS.personalBest}
              title="Best Day"
              value={formatBigNumber(detail.bestDayVolume)}
            />
          </div>

          <div className="mt-6">
            <Panel
              title={`${volumeLabel} Per Day`}
              subtitle={`Last 30 days · ${detail.targetsHit || 0} target${
                (detail.targetsHit || 0) === 1 ? '' : 's'
              } hit (${detail.targetHitRate || 0}% of tracked days)`}
            >
              {hasSeries ? (
                <Chart
                  type="area"
                  height={320}
                  width="100%"
                  series={[{ name: volumeLabel, data: dailySeries }]}
                  options={seriesOptions}
                />
              ) : (
                <PanelEmpty>Nothing tracked in the last 30 days.</PanelEmpty>
              )}
            </Panel>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
            <Panel
              title="Challenges"
              subtitle="Every challenge this person joined"
              className="xl:col-span-2"
            >
              {detail.challenges?.length ? (
                <PanelTable
                  head={
                    <>
                      <th className="py-2 pr-3 font-medium">Challenge</th>
                      <th className="py-2 pr-3 font-medium">Joined</th>
                      <th className="py-2 pr-3 font-medium text-right">Tasks</th>
                      <th className="py-2 pr-3 font-medium text-right">{volumeLabel}</th>
                      <th className="py-2 font-medium text-right">Result</th>
                    </>
                  }
                >
                  {detail.challenges.map((row) => (
                    <tr key={row._id} className={PANEL_ROW_CLASS}>
                      <td className="py-3 pr-3">
                        <div className="min-w-0">
                          <p className="truncate font-medium" title={row.title}>
                            {row.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Chip className={challengePhaseChipClass(row.phase)}>
                              {row.phaseLabel}
                            </Chip>
                            <span className="text-xs text-gray-400">
                              {row.activeDays} active day{row.activeDays === 1 ? '' : 's'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {dateLabel(row.joinedAt)}
                      </td>
                      <td className="py-3 pr-3 text-right">
                        {row.tasksDone}/{row.taskTotal}
                        <span className="ml-1 text-xs text-gray-400">({row.taskProgress}%)</span>
                      </td>
                      <td className="py-3 pr-3 text-right">{formatBigNumber(row.volume)}</td>
                      <td className="py-3 text-right">
                        {row.completed ? (
                          <Chip className="bg-green-100 text-green-700">Completed</Chip>
                        ) : (
                          <Chip className="bg-gray-100 text-gray-700">In progress</Chip>
                        )}
                      </td>
                    </tr>
                  ))}
                </PanelTable>
              ) : (
                <PanelEmpty>This person hasn&apos;t joined a challenge yet.</PanelEmpty>
              )}
            </Panel>

            <Panel title="Rewards Won" subtitle="Prizes and wellpoints from your challenges">
              {detail.rewards?.length ? (
                <ul className="space-y-4">
                  {detail.rewards.map((reward) => (
                    <li key={reward._id} className="flex items-start gap-3">
                      {reward.image ? (
                        <img
                          src={reward.image}
                          alt=""
                          className="w-10 h-10 rounded-input object-cover shrink-0"
                        />
                      ) : null}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-shoorah-btn dark:text-white truncate">
                          {reward.reward || 'Reward'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {reward.challengeTitle || 'Challenge'} · {dateLabel(reward.wonAt)}
                        </p>
                        {reward.wellPoints > 0 ? (
                          <p className="text-xs text-gray-400 mt-0.5">
                            {reward.wellPoints.toLocaleString()} wellpoints
                          </p>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <PanelEmpty>No rewards won yet.</PanelEmpty>
              )}
            </Panel>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default CChallengesUserDetail;
